import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import './../../../Styles/Payment/CouponModal.css';
import { jwtDecode } from 'jwt-decode';
import CloseIcon from '../../../assets/svg/closeIcon';
import { useCart } from '../../../CartProvider';
import Tabbing from '.././Search/Tabbing';
import whiteRound from '../../../assets/svg/whiteRound.svg';
import axiosVendorInstance from '../../../axiosVendorInstance';
import OfferDetailsModal from '../Search/OfferDetailsModal';
import offerDummyImage from '../../../assets/omg-logo-grey.png';
import CouponDetailsModal from '../Search/CouponDetailModal';
import Loader from '../../../Components/Loader/Loader';

function CouponModal({
    open,
    handleClose,
    vendorId,
    setSelectedCoupon,
    setSelectedOffer,
    setSelectedOfferPercentage,
    setCouponID,
    setCouponsID,
    setOffersID,
    setSelectedCouponAmount,
    price
}) {
    const token = localStorage.getItem('authToken');
    const decodedToken = jwtDecode(token);
    const [loadingCoupons, setLoadingCoupons] = useState(true);
    const [loadingOffers, setLoadingOffers] = useState(true);
    const [coupons, setCoupons] = useState([]);
    const [offers, setOffers] = useState([]);
    const [activeTab, setActiveTab] = useState('coupons');
    const [couponErrorMessage, setCouponErrorMessage] = useState('');
    const [offerErrorMessage, setOfferErrorMessage] = useState('');
    const { setOfferModelPercentage } = useCart();
    const [animateModal, setAnimateModal] = useState(false);
    const [closing, setClosing] = useState(false); 
    const [highlightedCoupon, setHighlightedCoupon] = useState(null);
    const [highlightedOffer, setHighlightedOffer] = useState(null);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [openOfferModal, setOpenOfferModal] = useState(false);
    const [openCouponModal, setOpenCouponModal] = useState(false);
    
    const handleCloseOfferModal = () => {
        setOpenOfferModal(false);
        setSelectedOffer(null);
    };

    const handleCloseCouponModal = () => {
        setOpenCouponModal(false);
        setSelectedCoupon(null);
    };

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const fetchCoupons = async () => {
        let isMounted = true;
        try {
            setLoadingCoupons(true);
            const response = await axiosVendorInstance.get(`/vendor/coupon/vendor/${vendorId}?limit=50`,{skipAuth: true});
            if (isMounted) {
                setCoupons(response?.data?.message === 'No coupons found for the provided filters' ? [] : response?.data?.coupons || []);
            }
        } catch (error) {
            console.error('Error fetching coupons:', error);
        } finally {
            if (isMounted) setLoadingCoupons(false);
        }
        return () => { isMounted = false; };
    };

    const fetchOffers = async () => {
        try {
            setLoadingOffers(true);
            const response = await axiosVendorInstance.get(`/vendor/offers/vendor/${vendorId}`,{skipAuth: true});
            setOffers(response?.data?.offers);
        } catch (error) {
            console.error('Error fetching offers:', error);
            setOffers([]);
        } finally {
            setLoadingOffers(false);
        }
        setClosing(false);
        setTimeout(() => {
            setAnimateModal(true);
        }, 100);
    };

    useEffect(() => {
        fetchCoupons();
        fetchOffers();
    }, [vendorId]);

    const validateCoupon = async (couponID, userId) => {
        try {
            const response = await axiosVendorInstance.post(`/vendor/vendors/${vendorId}/check-coupon-redemption`, {
                coupon_id: couponID,
                user_id: userId,
                totalAmount: price
            });
            return response?.data;
        } catch (error) {
            console.error('Error validating coupon:', error);
            return { used: false };
        }
    };

    const validateOffer = async (offerID) => {
        try {
            const response = await axiosVendorInstance.post(`/vendor/offerredeption/${vendorId}?offer_id=${offerID}`, {
                amount: price,
            });
            return response?.data;   
        } catch (error) {
            console.error('Error validating coupon:', error);
            return {
                error: error?.response?.data?.error || 'Something went wrong validating the offer.',
            };
        }
    };

    const handleApplyOffer = async (offerID, offerTitle, offerPercentage) => {
        setHighlightedOffer(offerID);
        setOfferErrorMessage('');
        const redemptionStatus = await validateOffer(offerID);
        
        if (redemptionStatus?.redeemed) {
            setOfferErrorMessage(redemptionStatus.message || 'Offer has already been redeemed.');
            return;
        }
    
        if (redemptionStatus?.error) {
            setOfferErrorMessage(redemptionStatus.error);
            return;
        }
    
        setOfferModelPercentage(null);
        setSelectedOffer(offerTitle);
        setSelectedOfferPercentage(offerPercentage);
        setSelectedCoupon(null);
        setSelectedCouponAmount(null);
        setCouponErrorMessage('');
        setOfferErrorMessage('');
        handleClose();
        setCouponID(null);
        setOffersID(offerID);
    };

    const handleApplyCoupon = async (couponID, Coupon_code, discountAmount) => {
        setHighlightedCoupon(couponID); 
        setCouponErrorMessage('');

        if (discountAmount > price) {
            setCouponErrorMessage(
                screenWidth < 500
                    ? 'Discount is more than your total price.'
                    : 'This coupon cannot be applied as the discount exceeds the total amount!'
            );
            return;
        }
         
        const redemptionStatus = await validateCoupon(couponID, decodedToken?.id);

        if (redemptionStatus.error) {
            setCouponErrorMessage(redemptionStatus.error);
        } else {
            setSelectedCoupon(Coupon_code);
            setSelectedCouponAmount(discountAmount);
            setCouponsID(couponID);
            setOfferModelPercentage(null);
            setCouponErrorMessage(''); 
            handleClose(); 
            setSelectedOffer(null); 
            setSelectedOfferPercentage(null);
            setOffersID(null);
        }
    };

    if (!open) return null;

    const formatDateTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        const formattedDate = `${day}-${month}-${year}`;

        return `${formattedDate}`;
    };

    return (
        <>
        
            <Modal
                open={open}
                onClose={()=>{ 
                    setClosing(true);
                    handleClose();
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                style={{ backdropFilter: 'blur(10px)' }}
            >
                <Box className={`coupon-model-pop-up ${animateModal ? 'animate' : ''} ${closing ? 'close' : ''}`}>
                    <div className="title-cross-container">
                        <h1 className="apply-coupon-title">
                            {activeTab === 'coupons' ? 'Apply Coupon' : 'Apply Offer'}
                        </h1>
                        <CloseIcon
                            onClick={() => handleClose()}
                            className="cross-icon-location"
                        />
                    </div>
                    <div className="add-ons-line margin-20px"></div>
                    <div className="coupon-offer-container">
                        <Tabbing
                            titleOne="offers"
                            titleTwo="coupons"
                            defaultTab="coupons"
                            onTabChange={(tab) => setActiveTab(tab)}
                            wrapperClassName="tabbing-buttons-padding"
                        />

                        {activeTab === 'offers' && offerErrorMessage && (
                            <div className="couppon-offer-error-message">
                                {offerErrorMessage}
                            </div>
                        )}
                        {activeTab === 'coupons' && couponErrorMessage && (
                            <div className="couppon-offer-error-message">
                                {couponErrorMessage}
                            </div>
                        )}


                        {activeTab === 'coupons' ? (
                            <div className="coupon-modal">
                                {loadingCoupons ? (
                                    <div className="coupons-loader">
                                        <Loader />
                                    </div>
                                ) : (
                                    <>
                                        {coupons?.length === 0 ? (
                                            <div className="no-coupons-message">No coupons available</div>
                                        ) : (
                                            <ul className="coupon-main-container" style={{paddingTop: '0px'}}>
                                                {coupons?.length > 0 && (
                                                    coupons?.map((coupon) => (
                                                        <li
                                                            key={coupon?.ID}
                                                            className="offer-card-container"
                                                        >
                                                            <div 
                                                                className="coupon-code"  
                                                                onClick={() => {
                                                                    setHighlightedCoupon(coupon);
                                                                    setOpenCouponModal(true); 
                                                                }}     
                                                            >
                                                                {[1, 2, 3, 4, 5].map((num) => (
                                                                    <div key={num} className={`white-round-${num} white-round-common`}>
                                                                        <img src={whiteRound} alt="whiteRound" />
                                                                    </div>
                                                                ))}
                                                                {coupon?.Title?.slice(0, 10)?.toUpperCase() || 'DISCOUNT'}
                                                            </div>
                                                            <div className={`offer-details-containerss ${highlightedCoupon === coupon?.ID ? 'highlight-border' : ''}`}>
                                                                <div style={{cursor: 'pointer'}}
                                                                    onClick={() => {
                                                                        setHighlightedCoupon(coupon);
                                                                        setOpenCouponModal(true); 
                                                                    }} >
                                                                    <h2 
                                                                        className="offer-title"
                                                                        onClick={() => {
                                                                            setHighlightedCoupon(coupon);
                                                                            setOpenCouponModal(true); 
                                                                        }} 
                                                                    >
                                                                        {coupon?.Title?.length > 20
                                                                            ? `${coupon.Title.slice(0, 20)}...`
                                                                            : coupon?.Title}
                                                                    </h2>
                                                                    <div className="coupon-dashed-line"></div>
                                                                    <div className="valid-date-container">
                                                                        <p className='offer-date'>Discount Amount : ${coupon?.max_amount || 'N/A'}</p>
                                                                        <h6 className="offer-date">
                                                                        Valid from : {coupon?.Valid_from ? formatDateTime(coupon?.Valid_from) : 'No Validation'}
                                                                        </h6>
                                                                        <h6 className="offer-date">
                                                                        Valid to : {coupon?.Valid_through ? formatDateTime(coupon?.Valid_through) : 'No Validation'}
                                                                        </h6>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    className="apply-btn-coupon"
                                                                    onClick={() =>
                                                                        handleApplyCoupon(
                                                                            coupon?.ID,
                                                                            coupon?.Coupon_code,
                                                                            coupon?.max_amount
                                                                        )
                                                                    }
                                                                >
                            Apply
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ))
                                                )}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="coupon-modal">
                                {loadingOffers ? (
                                    <div className="coupons-loader">
                                        <Loader />
                                    </div>
                                ) : (
                                    <>
                                        {offers?.length === 0 ? (
                                            <div className="no-coupons-message">No offers available</div>
                                        ) : (
                                            <ul className="coupon-main-container">
                                                {offers?.map((offer) => (
                                                    <li
                                                        key={offer?.ID}
                                                        className={`coupon-card-container ${highlightedOffer === offer?.ID ? 'highlight-border' : ''}`}
                                                    >

                                                        <div 
                                                            className="offer-image"
                                                            onClick={() => {
                                                                setHighlightedOffer(offer);
                                                                setOpenOfferModal(true); 
                                                            }}    
                                                        >
                                                            <img
                                                                src={
                                                                    offer?.Avatar_key || offerDummyImage
                                                                }
                                                                alt={offer?.Title}
                                                            />
                                                            <div className="offer-image-overlay">
                                                                <div className="offer-image-overlay-text">
                                                                    {offer?.percentage}%
                                                                    <span className="offer-image-overlay-off-text">
                              OFF
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="offer-details-containers">
                                                            <h2 className="offer-title"
                                                                onClick={() => {
                                                                    setHighlightedOffer(offer);
                                                                    setOpenOfferModal(true); 
                                                                }}    
                                                            >
                                                                {offer?.Title && offer?.Title.length > 15
                                                                    ? `${offer?.Title.substring(0, 15)}...`
                                                                    : offer?.Title}
                                                            </h2>
                                                            <div 
                                                                className="valid-date-container"
                                                                onClick={() => {
                                                                    setHighlightedOffer(offer);
                                                                    setOpenOfferModal(true); 
                                                                }}    
                                                            >
                                                                <h6 className="offer-date">
                            Valid from : {offer?.valid_from ? formatDateTime(offer?.valid_from) : 'No Validation'}
                                                                </h6>
                                                                <h6 className="offer-date">
                            Valid to : {offer?.valid_to ? formatDateTime(offer?.valid_to) : 'No Validation'}
                                                                </h6>
                                                            </div>
                                                            <button
                                                                className="apply-btn-coupon"
                                                                onClick={() =>
                                                                    handleApplyOffer(
                                                                        offer?.ID,
                                                                        offer?.Title,
                                                                        offer?.percentage
                                                                    )
                                                                }
                                                            >
                          Apply
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </Box>
            </Modal>
            {highlightedOffer && (
                <OfferDetailsModal
                    open={openOfferModal} 
                    handleClose={handleCloseOfferModal}
                    offer={highlightedOffer}
                    paddingBottom = '15px'
                />
            )}
            {highlightedCoupon && (
                <CouponDetailsModal
                    open={openCouponModal} 
                    handleClose={handleCloseCouponModal}
                    coupon={highlightedCoupon}
                />
            )}
        </>
    );
}

export default CouponModal;
